/* eslint-disable react-hooks/set-state-in-effect */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Flag, Users, Handshake, ArrowRight, CalendarBlank, MapPin } from "@phosphor-icons/react";

const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const prettyDate = (s) => {
    if (!s) return "";
    const d = new Date(s);
    return isNaN(d) ? s.slice(0, 10) : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

// Self-fetching, self-hiding golf registration call-to-action.
// Pass `eventId` for a single event (used on the public event page) OR
// `orgSlug` to surface every golf-enabled event for an org (used on the storefront).
export default function GolfRegisterCTA({ eventId, orgSlug }) {
    const [events, setEvents] = useState([]);

    const load = useCallback(async () => {
        try {
            if (eventId) {
                const { data } = await api.get(`/golf/public/${eventId}`);
                if (data.registration_open || data.hole_sponsor_open) {
                    setEvents([{
                        event_id: eventId,
                        title: data.event?.title || "",
                        starts_at: data.event?.starts_at || "",
                        venue: data.event?.venue || "",
                        registration_open: data.registration_open,
                        team_price: data.team_price,
                        hole_sponsor_open: data.hole_sponsor_open,
                        hole_sponsor_price: data.hole_sponsor_price,
                    }]);
                } else {
                    setEvents([]);
                }
            } else if (orgSlug) {
                const { data } = await api.get(`/golf/public/org/${orgSlug}`);
                setEvents(data.events || []);
            }
        } catch (err) { console.error("GolfRegisterCTA load failed", err); setEvents([]); }
    }, [eventId, orgSlug]);
    useEffect(() => { load(); }, [load]);

    if (!events.length) return null;

    return (
        <section className="px-6 md:px-12 lg:px-24 py-12 max-w-6xl mx-auto" data-testid="golf-register-cta">
            <div className="grid gap-5">
                {events.map((ev) => (
                    <div key={ev.event_id} className="ed-card p-6 md:p-8 border-2 border-accent bg-accent/5"
                        data-testid={`golf-cta-${ev.event_id}`}>
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                            <div>
                                <p className="label-eyebrow text-accent flex items-center gap-2">
                                    <Flag size={16} weight="fill" /> Charity golf tournament
                                </p>
                                <h3 className="font-heading text-2xl md:text-3xl mt-2">
                                    {orgSlug ? ev.title : "Play in this tournament"}
                                </h3>
                                <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                                    {orgSlug && ev.starts_at && (
                                        <span className="flex items-center gap-1.5">
                                            <CalendarBlank size={15} className="text-accent" /> {prettyDate(ev.starts_at)}
                                        </span>
                                    )}
                                    {orgSlug && ev.venue && (
                                        <span className="flex items-center gap-1.5">
                                            <MapPin size={15} className="text-accent" /> {ev.venue}
                                        </span>
                                    )}
                                    <span>Sign up your foursome or put your business on a hole — paid securely online.</span>
                                </div>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                                {ev.registration_open && (
                                    <Link to={`/golf-register/${ev.event_id}`} className="btn-accent justify-center"
                                        data-testid={`golf-cta-register-${ev.event_id}`}>
                                        <Users size={16} /> Register a team — {money(ev.team_price)}
                                        <ArrowRight size={14} weight="bold" />
                                    </Link>
                                )}
                                {ev.hole_sponsor_open && (
                                    <Link to={`/golf-register/${ev.event_id}`} className="btn-ghost justify-center"
                                        data-testid={`golf-cta-sponsor-${ev.event_id}`}>
                                        <Handshake size={16} /> Sponsor a hole — {money(ev.hole_sponsor_price)}
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}
